import { useContext, useMemo, useState } from "react";
import { LocaleContext } from "../../../routing/LangRouter";
import { Accordion, AccordionDetails, AccordionSummary, Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

//import constants
import { IS_MOBILE } from "../../../constants/breakpoints";

//import general components
import BasePopover from "../base-popover/base-popover";
import ArrowIcon from "../arrow-icon/arrow-icon";

//import images and icons
import { ArrowLeft2, ArrowRight2, Global } from "iconsax-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Language options for the language selector
const LANGUAGE_OPTIONS = [
    { label: "FA", value: "fa", name: "فارسی" },
    { label: "EN", value: "en", name: "English" },
    { label: "AR", value: "ar", name: "العربیة" },
];

// List of authorized routes where the language selector should be displayed
const AUTHORIZED_ROUTES = [
    "/{locale}",
    "/{locale}/contact-us",
    "/{locale}/about-us",
    "/{locale}/rules",
    "/{locale}/submit-complaints",
    "/{locale}/flight-search/domestic",
    "/{locale}/flight-search/international",
    "/{locale}/hotel-search/domestic",
    "/{locale}/hotel-search/international",
    "/{locale}/login",
    "/{locale}/itinerary",
];

function LanguageSelector({ setOpen }) {
    //general variables
    const { locale, setLocale } = useContext(LocaleContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    //Popover
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpenPopover = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const isPopoverOpen = Boolean(anchorEl);
    const popoverId = isPopoverOpen ? "language-popover" : undefined;

    // Initialize flag state with current locale's flag resource
    const currentLanguage = useMemo(() => LANGUAGE_OPTIONS.find(lang => lang.value === locale) || LANGUAGE_OPTIONS[0], [locale]);

    //handle language change: update flag state and set new locale
    const handleLanguageChange = newLanguage => {
        setLocale(newLanguage.value);
        handleClosePopover();
        navigate(0);
    };

    // Check if the current route is in the list of authorized routes
    const isAuthorizedRoute = AUTHORIZED_ROUTES.includes(location.pathname.replace(locale, "{locale}"));

    if (!isAuthorizedRoute) return null;

    return (
        <div>
            {IS_MOBILE ? (
                <Accordion sx={{ boxShadow: "none", width: "100%" }}>
                    <AccordionSummary
                        className="header__menu-link"
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        sx={{
                            minHeight: "fit-content !important",
                            backgroundColor: "var(--white-gray-color)",
                            "& .MuiAccordionSummary-content": { margin: "10px 0 !important", columnGap: "0.5rem" },
                            "&:hover": {
                                backgroundColor: "var(--white-gray-color)",
                                borderRadius: "0",
                            },
                        }}
                    >
                        <Global size="20" color="var(--teal-color-18)" variant="Outline" />
                        {t("language")}
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: "0 16px" }}>
                        {LANGUAGE_OPTIONS.map(lang => {
                            return (
                                <span
                                    key={lang.value}
                                    className={`header__menu-link ${lang.value === currentLanguage.value ? "activeItem" : ""}`}
                                    onClick={() => {
                                        handleLanguageChange(lang);
                                        setOpen(false);
                                    }}
                                >
                                    {lang.value === currentLanguage.value && (
                                        <ArrowIcon
                                            icons={[ArrowLeft2, ArrowRight2]}
                                            isExpanded={locale === "en"}
                                            size="16"
                                            color="var(--primary-color)"
                                            variant="Bold"
                                        />
                                    )}
                                    {lang.name}
                                </span>
                            );
                        })}
                    </AccordionDetails>
                </Accordion>
            ) : (
                <>
                    <Grid
                        className={`header__language-selector ${isPopoverOpen ? "open" : ""}`}
                        container
                        item
                        xs="auto"
                        columnGap={1}
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={handleOpenPopover}
                    >
                        <Global size="20" color="var(--teal-color-18)" variant="Outline" />
                        <span>{currentLanguage.label}</span>
                    </Grid>
                    <BasePopover
                        id={popoverId}
                        open={isPopoverOpen}
                        anchorEl={anchorEl}
                        handleClose={handleClosePopover}
                        vertical="bottom"
                        horizontal={locale === "en" ? "left" : "right"}
                        radius="0 0 0.5rem 0.5rem"
                    >
                        <Grid container item xs={12} rowGap={0.5} direction="column" alignItems="center">
                            {LANGUAGE_OPTIONS.map(lang => (
                                <span
                                    key={lang.value}
                                    className={`header__menu-link ${lang.value === currentLanguage.value ? "activeItem" : ""}`}
                                    onClick={() => handleLanguageChange(lang)}
                                >
                                    {lang.name}
                                </span>
                            ))}
                        </Grid>
                    </BasePopover>
                </>
            )}
        </div>
    );
}

export default LanguageSelector;
